import { Button, Icon, Text } from "@ttc3k/trekker";
import { Trans, useTranslation } from "react-i18next";
import { Divider, Flex, styled } from "styled-system/jsx";
import { OperatorTypeFilter } from "./OperatorTypeFilter";
import { ListFilters } from "./useFilters";
import {
  FilterKey,
  type FilterScreenProps as AllFilterScreenProps,
} from "shared/components";
import { KeywordsFilter } from "./KeywordsFilter";
import { BackOfficeTagsFilter } from "./BackOfficeTagsFilter";
import { PricingFilter } from "./PricingFilter";
import { MediaQualityFilter } from "./MediaQualityFilter";
import { OpeningStatusFilter } from "./OpeningStatusFilter";
import { DaysOfOperationFilter } from "./DaysOfOperationFilter";
import { SeasonalityFilter } from "./SeasonalityFilter";
import { VisibilityFilter } from "./VisibilityFilter";
import { useParams } from "react-router-dom";
import { DigitalHealthFilter } from "./DigitalHealthFilter";
import { LocationFilter } from "./LocationFilter";
import { AmenitiesFilter } from "./AmenitiesFilter";

type FilterScreenProps = Omit<AllFilterScreenProps, "type"> & {
  screen: FilterKey;
  filters: ListFilters;
  onUpdateFilters: (filter: Partial<ListFilters>) => void;
};

export const FilterScreen = ({
  onScreenChange,
  currentScreenFilterOption,
  screen,
  onUpdateFilters,
  filters,
}: FilterScreenProps) => {
  const { t } = useTranslation();
  const { organizationId } = useParams();

  const getCurrentFilterScreen = (currScreen: FilterKey) => {
    switch (currScreen) {
      case "OPERATOR_TYPE":
        return (
          <OperatorTypeFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "KEYWORD_TAGS":
        return (
          <KeywordsFilter filters={filters} onUpdateFilters={onUpdateFilters} />
        );
      case "AMENITY_TAGS":
        return (
          <AmenitiesFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "BACK_OFFICE_TAGS":
        return (
          <BackOfficeTagsFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "PRICING":
        return (
          <PricingFilter filters={filters} onUpdateFilters={onUpdateFilters} />
        );
      case "MEDIA_QUALITY":
        return (
          <MediaQualityFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "OPENING_STATUS":
        return (
          <OpeningStatusFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "DAYS_OF_OP":
        return (
          <DaysOfOperationFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "SEASONALITY":
        return (
          <SeasonalityFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "OPERATOR_VISIBILITY":
        return (
          <VisibilityFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "DIGITAL_HEALTH":
        return (
          <DigitalHealthFilter
            filters={filters}
            onUpdateFilters={onUpdateFilters}
          />
        );
      case "LOCATION":
        return (
          <LocationFilter filters={filters} onUpdateFilters={onUpdateFilters} />
        );
    }
  };
  return (
    <>
      <Flex flexDir={"column"} gap={"100"}>
        {/* Heading w/ back button */}
        <Flex
          gap={"100"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Flex gap={"100"} alignItems={"center"}>
            {currentScreenFilterOption && (
              <Icon
                Element={currentScreenFilterOption.icon}
                color={"icon.mid"}
                size={"lg"}
              />
            )}
            <Text visual={"bodySemiBold"} color={"text.dark"}>
              {t(`core:FILTER.${screen}.TITLE`)}
            </Text>
          </Flex>

          <Button
            mode={"light"}
            size={"sm"}
            onClick={() => onScreenChange("ALL")}
          >
            {t("core:BACK")}
          </Button>
        </Flex>

        {/* Description */}
        <Text visual={"smallMedium"} color={"text.mid"}>
          {screen === "BACK_OFFICE_TAGS" ? (
            <Trans
              i18nKey={`core:FILTER.${screen}.DESCRIPTION`}
              components={{
                Link: (
                  <styled.a
                    textDecoration={"underline"}
                    href={`/organization/${organizationId}/settings`}
                  />
                ),
              }}
            />
          ) : (
            t(`core:FILTER.${screen}.DESCRIPTION`)
          )}
        </Text>
      </Flex>
      <Divider borderColor={"border.light"} w={"full"} />
      {/* Filter options: */}
      {getCurrentFilterScreen(screen)}
    </>
  );
};
